import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell';
import { resetRequest } from '../api/auth';
import { ApiError } from '../api/client';

/**
 * Password reset - request stage (email entry → "we sent it").
 *
 * Both states live in this component so we don't need a second URL
 * for the confirmation. The server always returns 200 (security:
 * never leak which emails have accounts) so the confirmation says
 * "if an account exists" rather than confirming the address.
 */
export function ResetRequest() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (e: string) => resetRequest(e),
    onSuccess: () => setSent(true),
    onError: (err) => {
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    mutation.mutate(email.trim().toLowerCase());
  }

  if (sent) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle="If an account exists for that email, we&apos;ve sent instructions to reset your password."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <p
          style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-dim)',
            lineHeight: 'var(--lh-normal)',
            textAlign: 'center',
          }}
        >
          The link expires in 1 hour. If you don&apos;t see the email
          within a few minutes, check your spam folder or try again.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll send you a link to reset your password."
      footer={<Link to="/login">Back to sign in</Link>}
    >
      {errorMessage && (
        <div className="auth-alert auth-alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <input
          className="auth-input"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={mutation.isPending}
          required
        />
        <button
          type="submit"
          className="auth-btn"
          disabled={mutation.isPending || !email}
        >
          {mutation.isPending ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
    </AuthShell>
  );
}
